import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const FFBindingModal = ({ isOpen, onClose, onStatusUpdate, userData }) => {
    const [ffName, setFfName] = useState(userData?.ffName || '');
    const [ffUID, setFfUID] = useState(userData?.ffUID || '');
    const [loading, setLoading] = useState(false);
    const [verifyMode, setVerifyMode] = useState('selection'); // 'selection', 'manual', 'everify'
    const [fetchedPlayer, setFetchedPlayer] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const [eVerifyUid, setEVerifyUid] = useState('');
    const [syncingStatus, setSyncingStatus] = useState("");
    const [syncProgress, setSyncProgress] = useState(0);

    // Update state when userData changes
    React.useEffect(() => {
        if (userData) {
            setFfName(userData.ffName || '');
            setFfUID(userData.ffUID || '');
        }
    }, [userData]);

    // Animation Logic for Syncing
    React.useEffect(() => {
        let interval;
        if (isFetching) {
            const statuses = [
                "Connecting to Global API...",
                `Searching for UID: ${eVerifyUid}`,
                "Scanning Server Regions...",
                "Retrieving Player Metadata...",
                "Decoding Nickname Patterns...",
                "Mapping Experience Data...",
                "Almost there, finalizing sync..."
            ];
            let step = 0;
            setSyncingStatus(statuses[0]);
            setSyncProgress(0);

            interval = setInterval(() => {
                step++;
                if (step < statuses.length) {
                    setSyncingStatus(statuses[step]);
                }
                setSyncProgress(prev => Math.min(prev + (100 / (statuses.length + 1)), 96));
            }, 3000);
        } else {
            setSyncingStatus("");
            setSyncProgress(0);
        }
        return () => clearInterval(interval);
    }, [isFetching, eVerifyUid]);

    // Reset mode on open
    React.useEffect(() => {
        if (isOpen) {
            setVerifyMode('selection');
            setFetchedPlayer(null);
            setEVerifyUid('');
        }
    }, [isOpen]);

    // ✅ PREVENT SCROLLING WHEN MODAL IS OPEN
    React.useEffect(() => {
        const isVerifying = userData?.ffStatus === 'under_verification';
        if (isOpen || isVerifying) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, userData?.ffStatus]);

    if (!isOpen && userData?.ffStatus !== 'under_verification') return null;

    // If verified, show success state
    if (userData?.ffStatus === 'verified' && !userData?.ffVerificationSeen) {
        const handleThankYou = async () => {
            try {
                const response = await axios.put(`${backendUrl}/api/user/mark-ff-seen`);
                if (response.data.success) {
                    if (onStatusUpdate) onStatusUpdate({ ...userData, ffVerificationSeen: true });
                    if (window.refreshUserProfile) window.refreshUserProfile();
                    onClose();
                }
            } catch (error) {
                console.error("Failed to mark verification as seen", error);
                onClose(); // Still close to not block user
            }
        };

        return (
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-xl z-[110] flex items-center justify-center animate-in fade-in duration-300">
                <div className="bg-gray-900 border border-gray-800 w-full max-w-lg rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl relative m-4">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                    <div className="p-8 sm:p-10 text-center">
                        <div className="w-20 h-20 bg-green-500/10 rounded-2xl flex items-center justify-center mb-6 border border-green-500/20 mx-auto">
                            <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Verified Successfully</h3>
                        <p className="text-gray-400 text-sm mb-8">Your Free Fire identity has been verified. You are cleared for all tournaments.</p>
                        <button
                            onClick={handleThankYou}
                            className="w-full py-3.5 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg shadow-green-900/40"
                        >
                            Thank You, Arena
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // If already under verification, show the verification status page content/modal
    if (userData?.ffStatus === 'under_verification') {
        return (
            <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-xl z-[110] flex items-center justify-center animate-in fade-in duration-300">
                <div className="bg-gray-900 border border-gray-800 w-full max-w-md rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl relative m-4">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 via-red-500 to-yellow-500 animate-pulse"></div>
                    <div className="p-8 text-center">
                        <div className="w-20 h-20 bg-yellow-500/10 rounded-2xl flex items-center justify-center mb-6 border border-yellow-500/20 mx-auto animate-bounce duration-[2000ms]">
                            <i className="ri-radar-line text-4xl text-yellow-500"></i>
                        </div>

                        <h3 className="text-xl font-bold text-white mb-4">
                            Account Under Review
                        </h3>

                        <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700/50 mb-8">
                            <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                Our team is verifying your identity to ensure fair play.
                            </p>
                            <div className="flex flex-col gap-2 text-left bg-black/40 p-4 rounded-lg border border-white/5">
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-gray-500">FF NAME</span>
                                    <span className="font-medium text-white">{userData.ffName}</span>
                                </div>
                                <div className="flex justify-between items-center border-t border-white/5 pt-2 text-xs">
                                    <span className="text-gray-500">UID</span>
                                    <span className="font-medium text-yellow-500">{userData.ffUID}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 py-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20 text-xs font-semibold text-yellow-500 uppercase tracking-wide">
                            <i className="ri-time-line animate-spin"></i>
                            <span>Awaiting Verification</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const handleManualSubmit = async (e) => {
        e.preventDefault();

        if (!ffName || !ffUID) {
            toast.error("All combat details are required!");
            return;
        }

        if (ffUID.length < 5 || ffUID.length > 12 || !/^\d+$/.test(ffUID)) {
            toast.error("Invalid Free Fire UID!");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${backendUrl}/api/user/bind-ff`, { ffName, ffUID });
            if (response.data.success) {
                toast.success("Identity submitted for verification!");
                if (onStatusUpdate) onStatusUpdate(response.data.user);
                if (window.refreshUserProfile) window.refreshUserProfile();
                if (window.refreshProfilePage) window.refreshProfilePage();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to submit request");
        } finally {
            setLoading(false);
        }
    };

    const handleFetchEVerify = async () => {
        if (!eVerifyUid || eVerifyUid.length < 5) {
            toast.error("Please enter a valid UID");
            return;
        }

        setIsFetching(true);
        setFetchedPlayer(null);
        try {
            // Using backend proxy to bypass CORS
            const response = await axios.get(`${backendUrl}/api/user/fetch-ff-info`, {
                params: { uid: eVerifyUid }
            });

            if (response.data && response.data.success && response.data.data.basicInfo) {
                const info = response.data.data.basicInfo;
                setFetchedPlayer({
                    name: info.nickname,
                    uid: info.accountId,
                    level: info.level,
                    region: info.region
                });
            } else {
                toast.error(response.data.message || "Player not found on Free Fire servers");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Free Fire server connection failed");
        } finally {
            setIsFetching(false);
        }
    };

    const handleConfirmEVerify = async () => {
        if (!fetchedPlayer) return;

        setLoading(true);
        try {
            const response = await axios.post(`${backendUrl}/api/user/auto-verify-ff`, {
                ffName: fetchedPlayer.name,
                ffUID: fetchedPlayer.uid
            });

            if (response.data.success) {
                toast.success("Account verified instantly!");
                if (onStatusUpdate) onStatusUpdate(response.data.user);
                if (window.refreshUserProfile) window.refreshUserProfile();
                if (window.refreshProfilePage) window.refreshProfilePage();
                onClose();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "E-Verification failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-xl z-[110] flex items-center justify-center animate-in fade-in zoom-in duration-300">
            <div className="bg-gray-900 border border-gray-700 w-full max-w-lg rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl m-4 relative">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-yellow-500 p-6 sm:p-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-lg sm:text-xl font-bold text-white">
                                {verifyMode === 'selection' ? 'Verify Identity' :
                                    verifyMode === 'everify' ? 'E-Verification' : 'Manual Binding'}
                            </h3>
                        </div>
                        <button onClick={onClose} className="bg-black/20 hover:bg-black/40 text-white w-8 h-8 rounded-full flex items-center justify-center transition-all">
                            <i className="ri-close-line text-xl"></i>
                        </button>
                    </div>
                </div>

                <div className="p-6 sm:p-8">
                    {/* Mode Selection */}
                    {verifyMode === 'selection' && (
                        <div className="space-y-3">
                            {userData?.ffStatus === 'rejected' && (
                                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex gap-3 items-center">
                                    <i className="ri-error-warning-line text-red-500"></i>
                                    <p className="text-gray-400 text-xs">Last request rejected: {userData.ffRejectionReason}</p>
                                </div>
                            )}

                            <button
                                onClick={() => setVerifyMode('everify')}
                                className="w-full p-5 bg-blue-600/10 border border-blue-500/20 rounded-2xl text-left hover:bg-blue-600/20 transition-all group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400 group-hover:scale-105 transition-transform text-xl">
                                        <i className="ri-flashlight-line"></i>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-white font-semibold text-sm">Quick E-Verify</h4>
                                        <p className="text-gray-500 text-xs mt-0.5">Instant approval via FF Server check</p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-gray-600"></i>
                                </div>
                            </button>

                            <button
                                onClick={() => setVerifyMode('manual')}
                                className="w-full p-5 bg-gray-800/50 border border-gray-700/50 rounded-2xl text-left hover:bg-gray-800/80 transition-all group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-gray-700/50 rounded-xl flex items-center justify-center text-gray-400 text-xl">
                                        <i className="ri-edit-line"></i>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-white font-semibold text-sm">Manual Binding</h4>
                                        <p className="text-gray-500 text-xs mt-0.5">Manually enter details for review</p>
                                    </div>
                                    <i className="ri-arrow-right-s-line text-gray-600"></i>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Quick E-Verify Flow */}
                    {verifyMode === 'everify' && (
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500 pl-1">Free Fire UID</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"></i>
                                        <input
                                            type="text"
                                            value={eVerifyUid}
                                            onChange={(e) => setEVerifyUid(e.target.value.replace(/\D/g, ''))}
                                            placeholder="Enter UID"
                                            className="w-full pl-11 pr-4 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-blue-500 outline-none transition-all text-sm"
                                        />
                                    </div>
                                    <button
                                        onClick={handleFetchEVerify}
                                        disabled={isFetching || !eVerifyUid}
                                        className="px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold uppercase text-xs disabled:opacity-50 transition-all"
                                    >
                                        {isFetching ? <i className="ri-loader-4-line animate-spin"></i> : 'Fetch'}
                                    </button>
                                </div>
                            </div>

                            {/* Loading State Animation - UPGRADED */}
                            {isFetching && (
                                <div className="py-10 px-6 flex flex-col items-center justify-center bg-gray-800/20 rounded-2xl border border-white/5 relative overflow-hidden">
                                    {/* Animated Background Pulse */}
                                    <div className="absolute inset-0 bg-blue-500/5 animate-pulse"></div>

                                    <div className="relative mb-6">
                                        <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center border border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.1)]">
                                            <i className="ri-radar-line text-3xl text-blue-500 animate-spin-slow"></i>
                                        </div>
                                    </div>

                                    <div className="text-center w-full relative z-10">
                                        <p className="text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4 h-4">
                                            {syncingStatus}
                                        </p>

                                        {/* Progress Bar */}
                                        <div className="bg-gray-700 h-1.5 w-full rounded-full overflow-hidden border border-white/5 mb-3">
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600 bg-[length:200%_100%] animate-gradient transition-all duration-1000 ease-out"
                                                style={{ width: `${syncProgress}%` }}
                                            ></div>
                                        </div>

                                        <div className="flex justify-between text-[8px] font-bold uppercase tracking-widest text-gray-500">
                                            <span>Scanning Servers</span>
                                            <span className="text-blue-500">{Math.round(syncProgress)}%</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Free Fire Style Result Card */}
                            {fetchedPlayer && !isFetching && (
                                <div className="bg-[#1a1a1a] border-l-2 border-yellow-500 rounded-r-xl overflow-hidden animate-in slide-in-from-left duration-500 shadow-xl">
                                    <div className="bg-yellow-500/10 py-2 px-4 flex justify-between items-center border-b border-white/5">
                                        <span className="text-[10px] font-medium text-yellow-500 uppercase tracking-widest">Identity Authenticated</span>
                                        <div className="flex gap-1">
                                            <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></div>
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        <div className="flex items-center gap-4 mb-6">
                                            {/* Profile Avatar Box */}
                                            <div className="relative">
                                                <div className="w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center border border-white/5">
                                                    <i className="ri-shield-user-line text-yellow-500 text-3xl"></i>
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 bg-yellow-500 text-[8px] font-bold text-black px-1 rounded">
                                                    LV.{fetchedPlayer.level}
                                                </div>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-lg text-white truncate leading-tight mb-1">
                                                    {fetchedPlayer.name}
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-gray-500">
                                                        {fetchedPlayer.region}
                                                    </span>
                                                    <div className="h-1 w-1 bg-gray-800 rounded-full"></div>
                                                    <span className="text-[10px] text-gray-500 uppercase tracking-tight">Global Server</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Data Grid */}
                                        <div className="bg-black/20 rounded-xl border border-white/5 overflow-hidden mb-6">
                                            <div className="grid grid-cols-2 divide-x divide-white/5">
                                                <div className="p-3">
                                                    <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">UID</p>
                                                    <p className="text-xs font-medium text-gray-300">{fetchedPlayer.uid}</p>
                                                </div>
                                                <div className="p-3">
                                                    <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">Account</p>
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                                        <span className="text-[10px] text-green-500 font-medium uppercase">Verified</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 mt-8">
                                            <button
                                                onClick={() => setFetchedPlayer(null)}
                                                className="flex-1 py-3 text-gray-500 hover:text-gray-400 font-medium text-xs transition-colors"
                                            >
                                                Wrong Info?
                                            </button>
                                            <button
                                                onClick={handleConfirmEVerify}
                                                disabled={loading}
                                                className="flex-[2] py-3 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-bold text-xs transition-all shadow-lg flex items-center justify-center gap-2"
                                            >
                                                {loading ? 'Verifying...' : 'Confirm Identity'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!fetchedPlayer && !isFetching && (
                                <button
                                    onClick={() => setVerifyMode('selection')}
                                    className="w-full text-center text-gray-500 text-[10px] uppercase font-bold tracking-widest hover:text-gray-400 transition-colors"
                                >
                                    <i className="ri-arrow-left-s-line mr-1"></i> Back to selection
                                </button>
                            )}
                        </div>
                    )}

                    {/* Manual Binding Flow */}
                    {verifyMode === 'manual' && (
                        <form onSubmit={handleManualSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500 pl-1">Free Fire Name</label>
                                <div className="relative">
                                    <i className="ri-user-heart-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"></i>
                                    <input
                                        type="text"
                                        value={ffName}
                                        onChange={(e) => setFfName(e.target.value)}
                                        placeholder="Enter Name"
                                        className="w-full pl-11 pr-4 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-red-500 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-500 pl-1">Free Fire UID</label>
                                <div className="relative">
                                    <i className="ri-fingerprint-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"></i>
                                    <input
                                        type="text"
                                        maxLength={12}
                                        value={ffUID}
                                        onChange={(e) => setFfUID(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Enter UID"
                                        className="w-full pl-11 pr-4 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-yellow-500 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>

                            <div className="bg-yellow-500/5 p-4 rounded-lg border border-yellow-500/10 mt-6">
                                <p className="text-[10px] text-gray-400 leading-relaxed">
                                    Manual review takes 12-24h. Use <span className="text-blue-400">E-Verify</span> for instant access.
                                </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3.5 bg-gradient-to-r from-red-600 to-yellow-500 rounded-xl font-bold uppercase text-white shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 text-xs"
                                >
                                    {loading ? 'Submitting...' : 'Submit Request'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setVerifyMode('selection')}
                                    className="w-full text-center text-gray-500 text-[10px] uppercase font-bold tracking-widest hover:text-gray-400 transition-colors"
                                >
                                    Back to selection
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FFBindingModal;
